import { motion, useAnimate, useMotionValue, useTransform } from 'framer-motion';
import { useEffect, useState } from 'react';
import { AnimatedListItem } from '../../../shared/components/AnimatedListItem';
import { ProgressBar } from '../../../shared/components/ProgressBar';

interface ProgressStep {
    to: number;
    duration: number;
    listItemIndex?: number; // Optional: which list item to show at this step
}

export interface ListItemData {
    text: string;
    isVisible: boolean;
}

export interface PersonalizingProgressDisplayProps {
    title: string;
    progressMessages: string[];
    listItems: ListItemData[];
    hideWhenComplete?: boolean;
    onAnimationComplete: () => void;
    beforeAnimationComplete?: () => Promise<void>;
}

export const PersonalizingProgressDisplay = ({
    title,
    progressMessages,
    listItems: outsideListItems,
    hideWhenComplete = true,
    onAnimationComplete,
    beforeAnimationComplete = undefined
}: PersonalizingProgressDisplayProps) => {
    const [progressMessage, setProgressMessage] = useState<string>("...");
    const [listItems, setListItems] = useState<ListItemData[]>(outsideListItems);
    const count = useMotionValue(0);
    const rounded = useTransform(count, (latest) => Math.round(latest));
    const displayValue = useTransform(rounded, (latest) => `${latest}%`);
    const [scope, animate] = useAnimate();

    const handleProgressAnimation = async () => {
        // Base durations for normal speed
        const baseDurations = [0.9, 1, 1.4, 1.2, 2.8, 0.8];
        const slowMultiplier = 2.5; // How much slower when promise is pending

        // Start tracking the promise if it exists
        let promiseResolved = false;
        if (beforeAnimationComplete) {
            // Start the promise but don't await it yet
            beforeAnimationComplete().then(() => {
                promiseResolved = true;
            });
        }

        // Define the progress steps with custom timing and list item triggers
        const progressSteps: ProgressStep[] = [
            { to: 10, duration: baseDurations[0], listItemIndex: 0 },
            { to: 33, duration: baseDurations[1], listItemIndex: 1 },
            { to: 67, duration: baseDurations[2], listItemIndex: 2 },
            { to: 89, duration: baseDurations[3], listItemIndex: 3 },
            { to: 99, duration: baseDurations[4] },
            { to: 100, duration: baseDurations[5] }
        ];

        // Animate through each step sequentially
        for (const step of progressSteps) {
            // Determine duration based on promise state
            const duration = (beforeAnimationComplete && !promiseResolved)
                ? step.duration * slowMultiplier
                : step.duration;

            await animate(count, step.to, {
                duration: duration,
                ease: "linear"
            });

            // Show the corresponding list item when this step completes
            if (step.listItemIndex !== undefined) {
                setListItems(prev => prev.map((item, index) =>
                    index === step.listItemIndex ? { ...item, isVisible: true } : item
                ));
            }

            setProgressMessage(progressMessages[step.listItemIndex ?? 4]);
        }

        // Make sure the promise is done before completing
        if (beforeAnimationComplete && !promiseResolved) {
            await beforeAnimationComplete;
        }

        if (hideWhenComplete) {
            await animate(scope.current, { opacity: 0 }, { duration: 0.5 });
        }
        onAnimationComplete();
    };

    useEffect(() => {
        handleProgressAnimation();
    }, []);

    return (
        <div ref={scope} className='flex-1 w-full flex flex-col justify-center items-center overflow-hidden'>
            <motion.h1 className='flex-shrink-0 text-5xl font-semibold mt-8 mb-2'>{displayValue}</motion.h1>
            <div className='flex-shrink-0 flex flex-col items-center gap-2 w-full'>
                <h2 className='text-osBlack font-semibold'>{title}</h2>
                <ProgressBar progress={count} isChunky />
                <motion.p
                    initial={{ opacity: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    animate={{ opacity: 1 }}
                    key={progressMessage}
                    className='text-osBlack text-xs'>{progressMessage}</motion.p>
            </div>
            <div className='flex-1 overflow-y-auto w-full mt-4 pt-4'>
                <div className='flex flex-col gap-2 w-full h-[250px]'>
                    {listItems.map((item, index) => (
                        item.isVisible && (
                            <AnimatedListItem
                                key={index}
                                text={item.text}
                                delay={0} // No delay since we control visibility through state
                            />
                        )
                    ))}
                </div>
            </div>
        </div>
    );
};